import React, { useEffect, useRef, useState } from 'react';
import type { Course, SpiritedSegment } from '../types';
import { getCartoTileUrl, computePathDirectionArrows, createDirectionArrowMarker } from '../utils/mapUtils';
import { normalizeSector } from '../utils/raceSector';
import { trimRouteSegment, getDistanceMeters } from '../utils/routeTrimmer';

declare const L: any;

interface GroupRideMapProps {
    course: Course;
    spiritedSegment?: SpiritedSegment;
    locationName?: string;
    height?: string;
}

const findNearestPathPoint = (lat: number, lon: number, path: [number, number][]) => {
    if (!path || path.length === 0) return { lat, lon, index: -1 };
    let minDistSq = Infinity;
    let bestPoint = path[0];
    let bestIdx = 0;
    for (let i = 0; i < path.length; i++) {
        const pt = path[i];
        const dLat = pt[0] - lat;
        const dLon = pt[1] - lon;
        const distSq = dLat * dLat + dLon * dLon;
        if (distSq < minDistSq) {
            minDistSq = distSq;
            bestPoint = pt;
            bestIdx = i;
        }
    }
    return { lat: bestPoint[0], lon: bestPoint[1], index: bestIdx };
};

const getCoordPair = (coord: any): [number, number] | null => {
    if (!coord) return null;
    if (Array.isArray(coord) && coord.length >= 2) {
        const lat = Number(coord[0]);
        const lon = Number(coord[1]);
        if (!isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0)) {
            return [lat, lon];
        }
    } else if (typeof coord === 'object') {
        const lat = Number(coord.lat ?? coord.latitude);
        const lon = Number(coord.lon ?? coord.lng ?? coord.longitude);
        if (!isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0)) {
            return [lat, lon];
        }
    }
    return null;
};

const GroupRideMap: React.FC<GroupRideMapProps> = ({
    course,
    spiritedSegment,
    locationName,
    height = '420px'
}) => {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<any>(null);
    const polylineRef = useRef<any>(null);
    const spiritedPolylineRef = useRef<any>(null);
    const markersGroupRef = useRef<any>(null);

    const [mapTileStyle, setMapTileStyle] = useState<'dark' | 'voyager' | 'satellite'>('dark');

    const hasPath = Boolean(course?.path && course.path.length > 0);
    const designatedStart = getCoordPair(course?.start);
    const designatedFinish = getCoordPair(course?.finish);

    // If the saved path still contains a personal approach ride (path[0] > 1.5km from meetup),
    // automatically slice to the designated start & finish so the view displays only the group ride loop
    const effectivePath = React.useMemo(() => {
        if (!hasPath || !course.path || !designatedStart || !designatedFinish) return course?.path || [];
        const distFromPathStart = getDistanceMeters(designatedStart, course.path[0]);
        if (distFromPathStart > 1500) {
            const res = trimRouteSegment(course.path, designatedStart, designatedFinish);
            if (res && res.trimmedPath.length > 1) {
                return res.trimmedPath;
            }
        }
        return course.path;
    }, [course?.path, designatedStart, designatedFinish, hasPath]);

    const hasEffectivePath = Boolean(effectivePath && effectivePath.length > 0);

    // Prioritize admin-designated meetup/start & finish coordinates over raw file track endpoints
    const meetupPt: [number, number] | null = designatedStart || (hasEffectivePath ? effectivePath[0] : null);
    const finishPt: [number, number] | null = designatedFinish || (hasEffectivePath ? effectivePath[effectivePath.length - 1] : null);

    const startCoords: [number, number] = meetupPt || [47.6062, -122.3321];

    // Initialize Leaflet Map
    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return;

        const map = L.map(mapContainerRef.current, {
            zoomControl: false,
        }).setView(startCoords, 13);

        L.control.zoom({ position: 'bottomright' }).addTo(map);

        mapRef.current = map;

        // Force invalidate size for modal / async container rendering
        setTimeout(() => {
            if (mapRef.current) mapRef.current.invalidateSize();
        }, 150);

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, []);

    // Tile Layer Management
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        // Remove existing tile layers
        map.eachLayer((layer: any) => {
            if (layer instanceof L.TileLayer) {
                map.removeLayer(layer);
            }
        });

        let tileUrl = getCartoTileUrl('dark_all');
        if (mapTileStyle === 'voyager') {
            tileUrl = getCartoTileUrl('rastertiles/voyager');
        } else if (mapTileStyle === 'satellite') {
            tileUrl = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
        }

        L.tileLayer(tileUrl, {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19
        }).addTo(map);
    }, [mapTileStyle]);

    // Render Route Polylines and Markers
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;

        // Clear previous layers
        if (polylineRef.current) {
            polylineRef.current.remove();
            polylineRef.current = null;
        }
        if (spiritedPolylineRef.current) {
            spiritedPolylineRef.current.remove();
            spiritedPolylineRef.current = null;
        }
        if (markersGroupRef.current) {
            markersGroupRef.current.clearLayers();
        } else {
            markersGroupRef.current = L.layerGroup().addTo(map);
        }

        const markersGroup = markersGroupRef.current;

        if (hasEffectivePath && effectivePath) {
            // Main Route Polyline (indigo, matches RaceMap route)
            const mainPolyline = L.polyline(effectivePath, {
                color: '#4f46e5', // indigo-600
                weight: 5,
                opacity: 0.85,
                lineCap: 'round',
                lineJoin: 'round'
            }).addTo(map);

            polylineRef.current = mainPolyline;

            // Fit map bounds to polyline, extending bounds to include designated meetup and finish points
            const bounds = mainPolyline.getBounds();
            if (meetupPt) bounds.extend(meetupPt);
            if (finishPt) bounds.extend(finishPt);
            map.fitBounds(bounds.pad(0.12));

            // Direction Arrows along main route
            const routeArrows = computePathDirectionArrows(effectivePath);
            routeArrows.forEach(arrow => {
                const marker = createDirectionArrowMarker(L, arrow, {
                    color: '#ffffff',
                    outlineColor: '#1e1b4b',
                    size: 14
                });
                marker.addTo(markersGroup);
            });
        } else if (meetupPt) {
            map.setView(meetupPt, 14);
        }

        // Render Start / Meetup Marker (Prioritizing Admin-Set Location)
        if (meetupPt) {
            const isLoop = Boolean(finishPt && Math.abs(meetupPt[0] - finishPt[0]) < 0.0005 && Math.abs(meetupPt[1] - finishPt[1]) < 0.0005);
            const startIcon = L.divIcon({
                className: 'custom-map-marker',
                html: `<div style="background-color: ${isLoop ? '#a855f7' : '#10b981'}; width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2.5px solid white; box-shadow: 0 4px 10px rgba(0,0,0,0.5); font-size: 18px;">${isLoop ? '🏁' : '📍'}</div>`,
                iconSize: [34, 34],
                iconAnchor: [17, 17]
            });
            L.marker(meetupPt, { icon: startIcon })
                .addTo(markersGroup)
                .bindTooltip(`<b>${isLoop ? 'Meetup & Finish Location (Loop)' : 'Meetup Location'}</b><br/>${locationName || (isLoop ? 'Start & Finish Point' : 'Start Point')}`, { permanent: false });

            // Finish Marker (if separate from meetup)
            if (finishPt && !isLoop) {
                const finishIcon = L.divIcon({
                    className: 'custom-map-marker',
                    html: `<div style="background-color: #ef4444; width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2.5px solid white; box-shadow: 0 4px 10px rgba(0,0,0,0.5); font-size: 18px;">🏁</div>`,
                    iconSize: [34, 34],
                    iconAnchor: [17, 17]
                });
                L.marker(finishPt, { icon: finishIcon })
                    .addTo(markersGroup)
                    .bindTooltip('<b>Ride End</b>', { permanent: false });
            }
        }

            // Render Race Sector if present
            if (spiritedSegment && spiritedSegment.startLat && spiritedSegment.finishLat) {
                const effSector = normalizeSector(spiritedSegment, course?.path) || spiritedSegment;
                const spiritedStart: [number, number] = [effSector.startLat, effSector.startLon];
                const spiritedFinish: [number, number] = [effSector.finishLat, effSector.finishLon];

                let spiritedPath: [number, number][] = [];
                if (hasEffectivePath && effectivePath && effectivePath.length > 0) {
                    const sNear = findNearestPathPoint(effSector.startLat, effSector.startLon, effectivePath);
                    const fNear = findNearestPathPoint(effSector.finishLat, effSector.finishLon, effectivePath);

                    if (sNear.index !== -1 && fNear.index !== -1) {
                        const minIdx = Math.min(sNear.index, fNear.index);
                        const maxIdx = Math.max(sNear.index, fNear.index);
                        spiritedPath = effectivePath.slice(minIdx, maxIdx + 1);
                    }
                }

                if (spiritedPath.length === 0) {
                    spiritedPath = [spiritedStart, spiritedFinish];
                }

                // Draw Race Sector sub-path following GPX route in solid cyan
                const spiritedPolyline = L.polyline(spiritedPath, {
                    color: '#06b6d4', // cyan-500
                    weight: 7,
                    opacity: 0.95,
                    lineCap: 'round',
                    lineJoin: 'round'
                }).addTo(map);

                spiritedPolylineRef.current = spiritedPolyline;

                // Direction Arrows along race sector
                const sectorArrows = computePathDirectionArrows(spiritedPath, 10);
                sectorArrows.forEach(arrow => {
                    const marker = createDirectionArrowMarker(L, arrow, {
                        color: '#ffffff',
                        outlineColor: '#083344',
                        size: 14
                    });
                    marker.addTo(markersGroup);
                });

                // Add Sector Start Marker
                const spiritedStartIcon = L.divIcon({
                    className: 'custom-spirited-marker',
                    html: `<div style="background-color: #06b6d4; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 0 12px #06b6d4; font-size: 16px;">⚡</div>`,
                    iconSize: [30, 30],
                    iconAnchor: [15, 15]
                });
                L.marker(spiritedStart, { icon: spiritedStartIcon })
                    .addTo(markersGroup)
                    .bindTooltip('<b>⚡ Sector Start</b><br/>Pace Accelerates Here', { permanent: false });

                // Add Sector End Marker
                const spiritedFinishIcon = L.divIcon({
                    className: 'custom-spirited-marker',
                    html: `<div style="background-color: #06b6d4; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 0 12px #06b6d4; font-size: 16px;">🏆</div>`,
                    iconSize: [30, 30],
                    iconAnchor: [15, 15]
                });
                L.marker(spiritedFinish, { icon: spiritedFinishIcon })
                    .addTo(markersGroup)
                    .bindTooltip('<b>🏁 Sector End</b><br/>Sprint / Finish Line', { permanent: false });
        }
    }, [course, spiritedSegment, locationName, hasPath, meetupPt, finishPt]);

    const handleFitBounds = () => {
        if (mapRef.current && polylineRef.current) {
            const bounds = polylineRef.current.getBounds();
            if (meetupPt) bounds.extend(meetupPt);
            if (finishPt) bounds.extend(finishPt);
            mapRef.current.fitBounds(bounds.pad(0.12));
        } else if (mapRef.current) {
            mapRef.current.setView(startCoords, 14);
        }
    };

    return (
        <div className="relative w-full rounded-xl overflow-hidden border border-gray-700 shadow-xl bg-gray-900" style={{ height }}>
            <div ref={mapContainerRef} className="w-full h-full z-0" />

            {/* Map Style & Reset Controls */}
            <div className="absolute top-3 right-3 z-[1000] flex items-center gap-2 bg-gray-900/90 backdrop-blur-md p-1.5 rounded-lg border border-gray-700 shadow-lg">
                <button
                    onClick={() => setMapTileStyle('dark')}
                    className={`px-2.5 py-1 text-[11px] font-semibold rounded transition-colors ${mapTileStyle === 'dark' ? 'bg-cyan-500 text-gray-950 shadow' : 'text-gray-400 hover:text-white'}`}
                >
                    Dark
                </button>
                <button
                    onClick={() => setMapTileStyle('voyager')}
                    className={`px-2.5 py-1 text-[11px] font-semibold rounded transition-colors ${mapTileStyle === 'voyager' ? 'bg-cyan-500 text-gray-950 shadow' : 'text-gray-400 hover:text-white'}`}
                >
                    Terrain
                </button>
                <button
                    onClick={() => setMapTileStyle('satellite')}
                    className={`px-2.5 py-1 text-[11px] font-semibold rounded transition-colors ${mapTileStyle === 'satellite' ? 'bg-cyan-500 text-gray-950 shadow' : 'text-gray-400 hover:text-white'}`}
                >
                    Satellite
                </button>
                {hasPath && (
                    <button
                        onClick={handleFitBounds}
                        className="px-2.5 py-1 text-[11px] font-bold text-cyan-400 hover:bg-gray-800 rounded border border-gray-700 transition-colors"
                        title="Reset Zoom to Fit Route"
                    >
                        ⤢ Fit Route
                    </button>
                )}
            </div>
        </div>
    );
};

export default GroupRideMap;
